import { useEffect, useState } from "react";
import axios from "axios";
import { FileDown, Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useSite } from "@/context/SiteContext";
import { withSession } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Before/After proposal PDF generator - lives in the top bar (row 2 on
 * mobile, next to the workspace switcher on desktop) so it is always at hand
 * while presenting. Self-contained: fetches its own page list + pair config.
 * Hidden for the seed workspace (the seed IS the mockup).
 */
export const ProposalPdfButton = () => {
  const { activeSite } = useSite();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const [pages, setPages] = useState(null);
  const [rows, setRows] = useState([]);

  const siteId = activeSite?.id;

  // Reset cached data whenever the workspace changes.
  useEffect(() => {
    setConfig(null);
    setPages(null);
    setRows([]);
  }, [siteId]);

  if (!activeSite || activeSite.is_seed) return null;

  const openPicker = async () => {
    setOpen(true);
    if (config && pages) return;
    try {
      const [cfgRes, tgtRes] = await Promise.all([
        axios.get(`${API}/sites/${siteId}/proposal/config`),
        axios.get(`${API}/sites/${siteId}/targets`),
      ]);
      const list = (tgtRes.data.targets || []).slice();
      list.sort((a, b) => (a.slug === "home" ? -1 : b.slug === "home" ? 1 : 0));
      setPages(list);
      setConfig(cfgRes.data);
      const slugSet = new Set(list.map((p) => p.slug));
      const defaults = (cfgRes.data.default_pairs || []).filter((p) => slugSet.has(p.slug));
      setRows(defaults.length ? defaults.map((p) => ({ ...p })) : [
        { slug: "home", label: "Homepage", live_url: `https://${cfgRes.data.domain}/` },
      ]);
    } catch (e) {
      toast.error("Could not load the proposal configuration.");
      setOpen(false);
    }
  };

  const updateRow = (i, field, value) => {
    setRows((prev) => prev.map((r, idx) => {
      if (idx !== i) return r;
      const next = { ...r, [field]: value };
      if (field === "slug") {
        const pg = (pages || []).find((p) => p.slug === value);
        if (pg) next.label = pg.label?.replace(/\s*\(.*\)$/, "") || value;
        if (config?.domain) next.live_url = `https://${config.domain}/${value === "home" ? "" : value + "/"}`;
      }
      return next;
    }));
  };

  const addRow = () => {
    if (rows.length >= 6) return;
    const used = new Set(rows.map((r) => r.slug));
    const nextPage = (pages || []).find((p) => !used.has(p.slug));
    if (!nextPage) return;
    setRows((prev) => [...prev, {
      slug: nextPage.slug,
      label: nextPage.label?.replace(/\s*\(.*\)$/, "") || nextPage.slug,
      live_url: `https://${config?.domain || activeSite.production_domain}/${nextPage.slug === "home" ? "" : nextPage.slug + "/"}`,
    }]);
  };

  const generatePdf = async () => {
    setOpen(false);
    setBusy(true);
    toast.info("Building the Before/After PDF — capturing live pages and mockups (~20-60s)…", { duration: 8000 });
    try {
      const { data } = await axios.post(`${API}/sites/${siteId}/proposal`, { pairs: rows }, { timeout: 240000 });
      toast.success(`PDF ready — ${data.pages} pages (${data.compared.join(", ")}).`, {
        action: { label: "Open PDF", onClick: () => window.open(withSession(`${API}/sites/${siteId}/proposal.pdf`), "_blank", "noopener") },
        duration: 12000,
      });
      window.open(withSession(`${API}/sites/${siteId}/proposal.pdf`), "_blank", "noopener");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "PDF generation failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        data-testid="preview-pdf-button"
        disabled={busy}
        onClick={openPicker}
        className="h-7 shrink-0 rounded-[8px] border-black/20 px-2.5 font-nunito text-[9px] font-extrabold uppercase tracking-wide text-[#383838] hover:bg-black/[0.04]"
      >
        {busy ? <Loader2 size={11} className="animate-spin" /> : <FileDown size={11} />}
        {busy ? "Building…" : "PDF"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="pair-picker-dialog" className="max-w-2xl rounded-[14px]">
          <DialogHeader>
            <DialogTitle className="font-nunito font-extrabold text-[#383838]">Before/After PDF — choose the page pairs</DialogTitle>
            <DialogDescription className="text-[12.5px]">
              Each row becomes one comparison spread: their live page on the left, your mockup on the right.
              {config?.last_generated && (
                <>
                  {" "}Last generated {new Date(config.last_generated).toLocaleString()} —{" "}
                  <a
                    data-testid="pair-picker-last-pdf-link"
                    href={withSession(`${API}/sites/${siteId}/proposal.pdf`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-bold text-[#0050c0] hover:underline"
                  >
                    open last PDF
                  </a>.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {!config || !pages ? (
            <div className="space-y-2 py-2">
              <Skeleton className="h-10 rounded-[8px]" />
              <Skeleton className="h-10 rounded-[8px]" />
            </div>
          ) : (
            <div className="max-h-[46vh] space-y-3 overflow-y-auto py-1 pr-1">
              {rows.map((row, i) => (
                <div key={i} data-testid={`pair-row-${i}`} className="rounded-[10px] border border-black/10 bg-[#fafafa] p-2.5">
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="w-[190px] space-y-1">
                      <Label className="font-nunito text-[9.5px] font-bold uppercase tracking-wide text-[#6e6e6e]">Mockup page</Label>
                      <Select value={row.slug} onValueChange={(v) => updateRow(i, "slug", v)}>
                        <SelectTrigger data-testid={`pair-row-${i}-slug`} className="h-9 rounded-[8px] bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-64">
                          {pages.map((p) => (
                            <SelectItem key={p.slug} value={p.slug}>/{p.slug === "home" ? "" : p.slug + "/"}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-[150px] space-y-1">
                      <Label className="font-nunito text-[9.5px] font-bold uppercase tracking-wide text-[#6e6e6e]">Page label</Label>
                      <Input data-testid={`pair-row-${i}-label`} value={row.label} onChange={(e) => updateRow(i, "label", e.target.value)} className="h-9 rounded-[8px] bg-white" />
                    </div>
                    <div className="min-w-[180px] flex-1 space-y-1">
                      <Label className="font-nunito text-[9.5px] font-bold uppercase tracking-wide text-[#6e6e6e]">Live URL (before)</Label>
                      <Input data-testid={`pair-row-${i}-live-url`} value={row.live_url} onChange={(e) => updateRow(i, "live_url", e.target.value)} className="h-9 rounded-[8px] bg-white font-mono text-[12px]" />
                    </div>
                    <button
                      type="button"
                      data-testid={`pair-row-${i}-remove`}
                      onClick={() => setRows((prev) => prev.filter((_, idx) => idx !== i))}
                      disabled={rows.length <= 1}
                      aria-label="Remove pair"
                      className="mb-2 text-[#9a9a9a] transition-colors duration-150 hover:text-[#c25a00] disabled:opacity-30"
                    >
                      <X size={15} />
                    </button>
                  </div>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="pair-picker-add-row"
                onClick={addRow}
                disabled={rows.length >= 6}
                className="rounded-[8px] border-dashed border-black/25 font-nunito text-[10.5px] font-extrabold text-[#6e6e6e]"
              >
                <Plus size={12} /> Add page pair
              </Button>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" data-testid="pair-picker-cancel" onClick={() => setOpen(false)} className="rounded-[10px] font-nunito text-xs font-extrabold">
              Cancel
            </Button>
            <Button
              type="button"
              data-testid="pair-picker-generate"
              disabled={!config || rows.length === 0 || rows.some((r) => !r.live_url || !r.label)}
              onClick={generatePdf}
              className="rounded-[10px] bg-[#f07000] font-nunito text-xs font-extrabold uppercase tracking-wide text-white hover:bg-[#d96400]"
            >
              <FileDown size={13} /> Generate PDF ({rows.length} {rows.length === 1 ? "spread" : "spreads"})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
